import { type ComponentPropsWithoutRef, forwardRef, memo } from 'react';

import { ImageSearchIcon } from '@air/next-icons';
import { cn } from '@air/utils-tailwind';

import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import {
  SearchCommandFilterCount,
  type SearchCommandFilterCountProps,
} from '~/components/SearchCommand/SearchCommandFilterCount';
import { SearchCommandModeSelector } from '~/components/SearchCommand/SearchCommandModeSelector';
import { SmartSearchImageSearchStatus } from '~/components/SearchCommand/SmartSearchImageSearchStatus/SmartSearchImageSearchStatus';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';

export type SearchCommandTriggerProps = ComponentPropsWithoutRef<'button'> &
  Partial<Pick<SearchCommandFilterCountProps, 'count'>> & {
    compact?: boolean;
    placeholder?: string;
    search?: string;
  };

export const _SearchCommandTrigger = forwardRef<HTMLButtonElement, SearchCommandTriggerProps>(
  ({ className, compact, count, placeholder, search, onClick, ...restOfProps }, ref) => {
    const { canUseSmartSearch } = usePrivateSearchContext();
    const { isActiveImageSearch, similarToImageSearchAvailable } = usePrivateSearchUIHelpers();
    const { filtersCanBeSaved } = useAppliedFiltersCount();

    return (
      <button
        className={cn(
          'relative flex w-full items-center gap-2 overflow-hidden whitespace-nowrap border border-grey-5 bg-grey-1 px-3 outline-none ring-2 ring-transparent transition-[box-shadow] hover:ring-black/[0.04] dark:hover:ring-white/[0.025]',
          compact ? 'h-8' : 'h-10',
          className,
          'rounded-8',
          canUseSmartSearch && 'pr-3 pl-2',
          isActiveImageSearch && 'gap-1',
        )}
        id="SEARCH_COMMAND_TRIGGER"
        data-testid="SEARCH_COMMAND_TRIGGER"
        data-value={search}
        ref={ref}
        onClick={isActiveImageSearch ? undefined : onClick}
        {...restOfProps}
      >
        <SearchCommandModeSelector search={search} />
        <span
          className={cn(
            'flex min-w-0 flex-1 items-center font-medium',
            compact ? 'text-14' : 'text-16',
            search ? 'text-grey-12' : 'text-grey-9',
          )}
        >
          {search && <span data-testid="SEARCH_COMMAND_TRIGGER_VALUE">{search}</span>}
          {!search && isActiveImageSearch && <SmartSearchImageSearchStatus />}
          {!search && !isActiveImageSearch && (
            <div data-testid="SEARCH_COMMAND_TRIGGER_PLACEHOLDER" className="truncate">
              {placeholder}
            </div>
          )}
        </span>
        {similarToImageSearchAvailable && !search && !isActiveImageSearch && !filtersCanBeSaved && (
          <ImageSearchIcon className="ml-auto size-4 shrink-0 text-grey-10" />
        )}
        {!!count && <SearchCommandFilterCount count={count} />}
      </button>
    );
  },
);

_SearchCommandTrigger.displayName = '_SearchCommandTrigger';

export const SearchCommandTrigger = memo(_SearchCommandTrigger);

SearchCommandTrigger.displayName = 'SearchCommandTrigger';
